import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  useGetCheckInObjectivesQuery,
  useGetMedicalProvidersQuery,
  useSaveTreatmentCheckInDraftMutation,
} from '@/services/api/treatmentService';
import {
  ProviderTreatmentStatus,
  SaveCheckInDraftPayload,
  TreatmentCheckInAIExtraction,
} from '@/services/types/treatment-types';

import {
  EditCheckInFormValues,
  formObjectiveToApiObjective,
  formProviderUpdateToApiProviderUpdate,
} from './types';

interface UseSaveCheckInDraftParams {
  form: UseFormReturn<EditCheckInFormValues>;
  matterId: string;
  checkInId: string;
}

export const useAutoSaveCheckInDraft = ({
  form,
  matterId,
  checkInId,
}: UseSaveCheckInDraftParams) => {
  const [saveDraft] = useSaveTreatmentCheckInDraftMutation();
  const [isSaving, setIsSaving] = useState(false);

  const watchedValues = form.watch();
  const debouncedValues = useDebounce(watchedValues, 3000);

  useEffect(() => {
    // Early exit if the form is untouched or currently submitting
    if (!form.formState.isDirty || form.formState.isSubmitting) {
      return;
    }

    const persistDraft = async (values: EditCheckInFormValues) => {
      setIsSaving(true);

      const payload: SaveCheckInDraftPayload = {
        check_in_id: checkInId,
        general_notes: values.general_notes,
        pain_level: values.pain_level === -1 ? null : values.pain_level,
        pain_level_note: values.pain_level_note,
        updates:
          values.provider_updates?.map((p) =>
            formProviderUpdateToApiProviderUpdate(p),
          ) ?? [],
        objectives:
          values.objectives?.map(formObjectiveToApiObjective) ?? [],
      };

      try {
        await saveDraft({ matterId, checkInId, payload }).unwrap();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to save draft', error);
      } finally {
        setIsSaving(false);
      }
    };

    if (debouncedValues) {
      // Fire-and-forget — we don't want to block the UI.
      persistDraft(debouncedValues);
    }
    // We explicitly depend on the debounced values and the dirty/submitting
    // flags, not the full form object, to avoid unnecessary re-invocations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValues, form.formState.isDirty, form.formState.isSubmitting]);

  return { isSaving };
};

// Actionable Summary Hook

export interface ActionableObjective {
  type: 'missing_provider' | 'treating_provider' | 'review_provider' | 'urgent';
  priority: 'high' | 'medium' | 'low';
  providerName?: string;
  providerId?: string;
  description: string;
  questions?: string[];
}

export interface ActionableSummary {
  objectives: ActionableObjective[];
  summaryText: string;
  summaryParts: string[];
  urgentCount: number;
  missingProvidersCount: number;
  treatingProvidersCount: number;
  reviewProvidersCount: number;
}

export const useActionableSummary = (
  matterId: string,
  checkInId: string | undefined,
  aiExtraction: TreatmentCheckInAIExtraction | undefined,
  reviewFoundProviders: any[] = [], // Add review found providers parameter
): ActionableSummary => {
  const { data: providers = [] } = useGetMedicalProvidersQuery(matterId);

  // Try to get AI-generated objectives from backend
  const { data: apiObjectives } = useGetCheckInObjectivesQuery(
    { matterId, checkInId: checkInId ?? '' },
    { skip: !checkInId }, // Skip API call if no checkInId
  );

  return useMemo(() => {
    // If API returned AI-generated objectives successfully, use them
    if (apiObjectives && apiObjectives.source === 'ai') {
      return {
        objectives: [], // Not used in UI, kept for backward compatibility
        summaryText: apiObjectives.objectives.join(', '),
        summaryParts: apiObjectives.objectives,
        urgentCount: 0, // Metadata not needed for AI-generated format
        missingProvidersCount: 0,
        treatingProvidersCount: apiObjectives.metadata.treating_count,
        reviewProvidersCount: apiObjectives.metadata.review_count,
      };
    }

    // FALLBACK: Use client-side logic if API fails or returns fallback
    const objectives: ActionableObjective[] = [];

    // Don't create individual objectives for review providers
    // They'll only appear in the summary text

    // Early return if no AI extraction
    if (!aiExtraction || aiExtraction.extraction_status !== 'completed') {
      const reviewCount = reviewFoundProviders.length;

      // Build summary with just review providers
      const summaryText =
        reviewCount > 0
          ? `${reviewCount} provider${reviewCount > 1 ? 's need' : ' needs'} review`
          : 'No specific action items identified. Continue with regular check-in questions.';

      const summaryParts =
        reviewCount > 0
          ? [
              reviewCount === 1
                ? 'one provider needs review'
                : `${reviewCount} providers need review`,
            ]
          : [];

      return {
        objectives,
        summaryText,
        summaryParts,
        urgentCount: 0,
        missingProvidersCount: 0,
        treatingProvidersCount: 0,
        reviewProvidersCount: reviewCount,
      };
    }

    // Find treating providers in our system
    const treatingProviders = providers.filter(
      (p) => p.treatment_status === ProviderTreatmentStatus.TREATING,
    );

    // Find providers mentioned in AI extraction
    const mentionedProviderNames = new Set(
      aiExtraction.provider_extractions.map((pe) =>
        pe.provider_name.toLowerCase(),
      ),
    );

    // 1. Missing providers - treating providers NOT mentioned in call
    treatingProviders.forEach((provider) => {
      if (!provider.name) return; // Skip providers without names

      const providerNameLower = provider.name.toLowerCase();
      const mentioned = Array.from(mentionedProviderNames).some(
        (mentionedName) =>
          providerNameLower.includes(mentionedName) ||
          mentionedName.includes(providerNameLower),
      );

      if (!mentioned) {
        objectives.push({
          type: 'missing_provider',
          priority: 'high',
          providerName: provider.name ?? undefined,
          providerId: provider.id,
          description: `${provider.name} - No update received`,
          questions: [
            'Are they still treating with this provider?',
            'When is their next appointment?',
            'Any changes in treatment frequency or status?',
          ],
        });
      }
    });

    // 2. Treating providers mentioned - ask follow-up questions
    aiExtraction.provider_extractions.forEach((extraction) => {
      const questions: string[] = [];

      // Generate contextual questions based on what we know
      if (extraction.treatment_status === 'TREATING') {
        if (!extraction.treatment_frequency) {
          questions.push('How often are they going?');
        }
        if (!extraction.next_appointment?.scheduled) {
          questions.push('When is their next appointment?');
        }
        if (!extraction.treatment_notes) {
          questions.push('How is treatment going? Any improvements or issues?');
        }
        if (
          extraction.treatment_notes &&
          (extraction.treatment_notes.toLowerCase().includes('pain') ||
            extraction.treatment_notes.toLowerCase().includes('symptom'))
        ) {
          questions.push('What is their current pain level (1-10)?');
          questions.push('Any specific symptoms or functional limitations?');
        }
      }

      if (extraction.treatment_status === 'COMPLETED') {
        if (!extraction.completion_info?.completion_date) {
          questions.push('When did treatment end?');
        }
        if (!extraction.completion_info?.reason) {
          questions.push('Why did treatment end?');
        }
      }

      if (extraction.treatment_status === 'REFERRED_TO') {
        if (!extraction.referral_info?.referred_by) {
          questions.push('Who referred them to this provider?');
        }
      }

      if (questions.length > 0) {
        objectives.push({
          type: 'treating_provider',
          priority: 'medium',
          providerName: extraction.provider_name,
          description: `${extraction.provider_name} - ${extraction.treatment_status || 'Status unknown'}`,
          questions,
        });
      }
    });

    // 3. Urgent items from AI extraction
    if (aiExtraction.how_claimant_feels?.severity?.value) {
      const painLevel = aiExtraction.how_claimant_feels.severity.value;
      if (painLevel >= 7) {
        objectives.push({
          type: 'urgent',
          priority: 'high',
          description: `High pain level reported (${painLevel}/10)`,
          questions: [
            'What is causing the high pain?',
            'Have they discussed pain management with their doctor?',
            'Are they able to function normally?',
          ],
        });
      }
    }

    if (
      aiExtraction.how_claimant_feels?.functional_impact?.reported &&
      aiExtraction.how_claimant_feels.functional_impact.details.length > 0
    ) {
      objectives.push({
        type: 'urgent',
        priority: 'medium',
        description: `Functional limitations reported`,
        questions: [
          `Specific issues: ${aiExtraction.how_claimant_feels.functional_impact.details.join(', ')}`,
          'Is this improving or getting worse?',
        ],
      });
    }

    // 4. Sort objectives by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    objectives.sort(
      (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority],
    );

    // Generate summary text
    const urgentCount = objectives.filter((o) => o.priority === 'high').length;
    const missingCount = objectives.filter(
      (o) => o.type === 'missing_provider',
    ).length;
    const treatingCount = objectives.filter(
      (o) => o.type === 'treating_provider',
    ).length;
    // Use actual count of review providers, not count of objectives
    const reviewCount = reviewFoundProviders.length;

    // Build a comprehensive summary with all action items in natural language
    const summaryParts: string[] = [];

    // Add review providers to summary in natural language
    if (reviewCount > 0) {
      summaryParts.push(
        reviewCount === 1
          ? 'one provider needs review'
          : `${reviewCount} providers need review`,
      );
    }

    // Add missing providers in natural language
    if (missingCount > 0) {
      const missingObjectives = objectives.filter(
        (o) => o.type === 'missing_provider',
      );
      if (missingObjectives.length === 1) {
        summaryParts.push(
          `${missingObjectives[0].providerName} hasn't been mentioned yet`,
        );
      } else if (missingObjectives.length === 2) {
        const names = missingObjectives
          .map((o) => o.providerName)
          .filter(Boolean);
        summaryParts.push(
          `${names[0]} and ${names[1]} haven't been mentioned yet`,
        );
      } else {
        summaryParts.push(
          `${missingCount} providers haven't been mentioned yet`,
        );
      }
    }

    // Add treating providers that need follow-up in natural language
    if (treatingCount > 0) {
      if (treatingCount === 1) {
        const provider = objectives.find((o) => o.type === 'treating_provider');
        summaryParts.push(
          `${provider?.providerName} needs follow-up questions answered`,
        );
      } else {
        summaryParts.push(
          `${treatingCount} providers need follow-up questions answered`,
        );
      }
    }

    // Add urgent items with conversational context
    const urgentObjectives = objectives.filter((o) => o.type === 'urgent');
    urgentObjectives.forEach((obj) => {
      if (obj.description.includes('pain level')) {
        const match = obj.description.match(/(\d+)\/10/);
        if (match) {
          summaryParts.push(
            `last time they reported pain at ${match[1]}/10 - check current level`,
          );
        } else {
          summaryParts.push('check their current pain level');
        }
      } else if (obj.description.includes('Functional limitations')) {
        summaryParts.push(
          'they mentioned functional limitations - see if improving',
        );
      } else {
        summaryParts.push(obj.description.toLowerCase());
      }
    });

    const summaryText =
      summaryParts.length > 0
        ? summaryParts.join(', ')
        : 'No specific action items identified. Continue with regular check-in questions.';

    return {
      objectives,
      summaryText,
      summaryParts, // Add bullet-point array
      urgentCount,
      missingProvidersCount: missingCount,
      treatingProvidersCount: treatingCount,
      reviewProvidersCount: reviewCount,
    };
  }, [apiObjectives, aiExtraction, providers, reviewFoundProviders]);
};
